import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import withLayout from "../layout";
import { postData } from "../../../backend/api";
import { clearParking, fetchParking } from "../../../store/actions/parking";
import logo from "../../../images/public/innopark24-logo.png";
import LanguageDropdown from "../../elements/language";
import "./login.css";

// Kennzeichen im bisherigen API-Format "ORTSKENNUNG-REST" (z. B. "LÖ-AB123").
// DE: Ortskennung muss mit Bindestrich oder Leerzeichen abgetrennt sein.
const buildPlate = (value, country) => {
  const input = value.trim().toUpperCase();
  if (country === "de") {
    const match = input.match(/^([A-ZÄÖÜ]{1,3})[\s-]+([A-ZÄÖÜ0-9][A-ZÄÖÜ0-9\s-]*)$/);
    if (!match) return null;
    const rest = match[2].replace(/[\s-]/g, "");
    if (rest.length < 2 || rest.length > 7) return null;
    return `${match[1]}-${rest}`;
  }
  const foreign = input.replace(/[\s-]/g, "");
  return /^[A-Z0-9]{5,12}$/.test(foreign) ? foreign : null;
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
const CrossIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 7l10 10M17 7L7 17" />
  </svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

const Login = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { parkingid } = useParams();
  const parking = useSelector((state) => state.parking);
  const { setLoaderBox } = props;

  const [country, setCountry] = useState("de");
  const [plate, setPlate] = useState("");
  const [plateError, setPlateError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [registeredPlate, setRegisteredPlate] = useState(null);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    document.title = "Gastparken | innopark24";
  }, []);

  useEffect(() => {
    dispatch(fetchParking(parkingid));
  }, [parkingid, dispatch]);

  useEffect(() => {
    setLoaderBox(parking.isLoading);
    if (parking.error !== null) {
      setIsValid(false);
      dispatch(clearParking());
    }
  }, [parking, setLoaderBox, dispatch]);

  const info = parking.data && parking.data.data;
  const hoursText = info ? `${info.hours} ${t("hours")}` : "";
  const validUntil = info ? info.datetime : "";
  const isGerman = country === "de";

  const onPlateChange = (event) => {
    setPlate(event.target.value.toUpperCase().replace(/[^A-ZÄÖÜ0-9\s-]/g, ""));
    setPlateError(false);
    setSubmitError("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const licensePlate = buildPlate(plate, country);
    if (!licensePlate) {
      setPlateError(true);
      return;
    }
    setSubmitError("");
    setLoaderBox(true);
    try {
      const response = await postData({ licensePlate }, "guest/extent");
      if (response.status === 200) {
        setRegisteredPlate(licensePlate);
      } else {
        setSubmitError(t("gpSubmitError"));
      }
    } catch (error) {
      setSubmitError(t("gpSubmitError"));
    }
    setLoaderBox(false);
  };

  let content = null;
  if (!isValid) {
    content = (
      <div className="ipl-result">
        <div className="ipl-result-icon bad">
          <CrossIcon />
        </div>
        <h1>{t("unautherized")}</h1>
        <p>{t("unautherizedDesc")}</p>
      </div>
    );
  } else if (info && registeredPlate) {
    content = (
      <div className="ipl-result">
        <div className="ipl-result-icon ok">
          <CheckIcon />
        </div>
        <h1>{t("successfull")}</h1>
        <p>{t("saved")}</p>
        <div className="ipl-facts">
          <div className="ipl-fact">
            <span>{t("licensePlate")}</span>
            <strong>{registeredPlate}</strong>
          </div>
          <div className="ipl-fact">
            <span>{t("validity")}</span>
            <strong>{validUntil}</strong>
          </div>
        </div>
        <p className="ipl-reminder">{t("info")}</p>
      </div>
    );
  } else if (info) {
    content = (
      <>
        <h1>{t("gpTitle")}</h1>
        <p className="ipl-lead">
          <strong>{t("gpLeadHead", { hours: info.hours })}</strong>
          <span>{t("gpLead")}</span>
        </p>

        {submitError && (
          <div className="ipl-status" role="alert">
            {submitError}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          <div className={`ipl-field ${plateError ? "has-error" : ""}`}>
            <label htmlFor="gp-plate">{t("licensePlate")}</label>
            <div className="ipl-plate-row">
              <div className="ipl-country">
                <select
                  aria-label={t("gpCountry")}
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setPlateError(false);
                  }}
                >
                  <option value="de">DE</option>
                  <option value="int">Int</option>
                </select>
              </div>
              <div className="ipl-input">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 17h14v-4l-2-6H7l-2 6z" />
                  <path d="M5 13h14M6 17v2M18 17v2" />
                </svg>
                <input id="gp-plate" type="text" autoComplete="off" autoCapitalize="characters" spellCheck="false" maxLength={14} placeholder={isGerman ? "M-AB1234" : "AB123CD"} value={plate} onChange={onPlateChange} />
              </div>
            </div>
            <div className="ipl-hint">{t(isGerman ? "gpPlateHintDe" : "gpPlateHintInt")}</div>
            <div className="ipl-err">{t(isGerman ? "gpPlateErrorDe" : "gpPlateErrorInt")}</div>
          </div>

          <div className="ipl-facts">
            <div className="ipl-fact">
              <span>{t("maximumParking")}</span>
              <strong>{hoursText}</strong>
            </div>
            <div className="ipl-fact">
              <span>{t("validity")}</span>
              <strong>{validUntil}</strong>
            </div>
          </div>

          <label className="ipl-check">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span dangerouslySetInnerHTML={{ __html: t("acceptAgreement") }}></span>
          </label>

          <button className="ipl-btn" type="submit" disabled={!agreed || parking.isLoading}>
            <span>{t("confirm")}</span>
          </button>
        </form>
      </>
    );
  }

  return (
    <div className="ipl-page">
      <div className="ipl-left">
        <header className="ipl-header">
          <a href="https://innopark24.com/" aria-label="innopark24">
            <img className="ipl-logo" src={logo} alt="innopark24" />
          </a>
          <LanguageDropdown label={t("gpLanguage")} />
        </header>

        <div className="ipl-form-wrap">
          <section className="ipl-card">{content}</section>
        </div>

        <footer className="ipl-footer">
          <a href="https://innopark24.com/impressum/">{t("imprint")}</a>
          <a href="https://innopark24.com/datenschutz/">{t("privacy")}</a>
          <a href="https://ticket.innopark24.de/faq">FAQ</a>
        </footer>
      </div>

      <aside className="ipl-right">
        <div className="ipl-info">
          <span className="ipl-eyebrow">{t("gpInfoEyebrow")}</span>
          <h2>{t("gpInfoHead")}</h2>
          <p className="ipl-info-lead">{t("gpInfoLead")}</p>
          <ul className="ipl-steps">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <span className="ipl-num">{n}</span>
                <div>
                  <strong>{t(`gpStep${n}Head`)}</strong>
                  <span>{t(`gpStep${n}Text`)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="ipl-note">
            <InfoIcon />
            <p>
              <strong>{t("gpNoteHead")}</strong>
              <span>{t("gpNoteText")}</span>
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default withLayout(Login);
